/**
 * The vote a seat has already cast, read back off its server-side action label.
 *
 * `playerMultiRunVote` lives only in React state, so a reconnect mid-window used
 * to re-arm the buttons and show no prior choice — a UX regression, and the
 * trigger for the double-vote path in EN-663 D1. The authoritative record is the
 * `p{seat}action` value voteMultiRun writes alongside the tally, which reaches
 * the client as `players[seat].action`; this maps it back to the vote value the
 * buttons and the send guard use.
 *
 * Returns '0' ("not voted") for every other action, which is what the betting
 * labels left over from the hand ("Call", "All In", '') correctly mean here.
 */
/**
 * How long a locally-recorded vote may go unechoed before the buttons come back.
 *
 * The client sends the vote and nothing answers: no route response on the
 * `voteMultiRun` Lambda, no reply channel on the engine writer. The only
 * positive evidence a vote landed is the seat's own action label coming back in
 * the next table frame, which is one emit + one write + one broadcast away —
 * frames arrive about once a second, so 3s is a few frames of grace rather than
 * a tuned round-trip budget.
 *
 * Erring long is the cheaper mistake in only one direction, and this is it: a
 * rollback that fires too early is self-correcting, because the echo is
 * authoritative and the very next frame carrying it puts the vote back. A
 * rollback that never fires leaves the player locked out of a window that is
 * already nearly over.
 *
 * ponytail: fixed grace, not an adaptive one off the observed frame cadence.
 * Worth revisiting only if tables are seen broadcasting slower than this.
 */
export const VOTE_ECHO_GRACE_MS = 3000;

/**
 * What this seat's vote should be, reconciling the server's echo against what
 * the client recorded locally. Returns null for "leave it alone".
 *
 * The echo ALWAYS wins when it is present. That covers the reconnect case this
 * file was written for (local state lost, server remembers), and it is also
 * what makes the rollback below safe to be wrong about: a vote cleared in error
 * is restored by the next frame that carries it.
 *
 * `unechoedForMs` is measured from the first frame observed with a local vote
 * and no echo, so it counts time in which the client was actually being told
 * things. When frames stop entirely this simply never advances and no rollback
 * happens, which is the correct conservative answer — a client that is not
 * receiving frames has no evidence either way, and the send guard
 * (rejectUnsendableTableCommand) is what stops an undeliverable vote from being
 * recorded in the first place.
 *
 * `windowOpen` is `runs` still carrying its 'v'. A vote belongs to ONE window, so
 * a closed one clears it outright, with no grace: there is no echo left to wait
 * for once the server has taken the 'v' off the row. That is what keeps the
 * previous hand's choice off the next hand's prompt. The per-hand resets in
 * Game's `hand` switch fire only on the frames carrying hand ''/1, and each of
 * those states lasts a single processor tick, so a client that misses one used to
 * reach the next window still holding the last one's vote: the option
 * pre-selected in green, the buttons disabled behind `playerMultiRunVote !== '0'`,
 * and the seat's real clicks dropped by GameService's send guard until the echo
 * grace expired.
 */
export const reconcileVote = (
  echoed: string,
  local: string,
  unechoedForMs: number,
  windowOpen: boolean
): string | null => {
  if (!windowOpen) return local === '0' ? null : '0';
  if (echoed !== '0') return echoed === local ? null : echoed;
  if (local === '0') return null;
  return unechoedForMs >= VOTE_ECHO_GRACE_MS ? '0' : null;
};

export const voteFromSeatAction = (action?: string): string => {
  switch (action) {
    // 'Run it Twice?'/'Run it Thrice?' are what both writers send, and they are
    // a WIRE CONTRACT, not copy — see the note at the writer. The player-facing
    // spelling is 2x/3x, and it deliberately does NOT reach this switch: this
    // value is never rendered (the seat pill is gated on VALID_ACTIONS, which
    // excludes it), so there is no reason to pay a rollout break to change it.
    //
    // The 2x/3x arms are kept as a one-way tolerance for rows written during
    // the window when the writers briefly sent that spelling. Cheap, and the
    // direction that matters: a reader that fails to recognise a cast vote
    // re-arms the buttons for a seat that has already voted.
    case 'Run it Twice?':
    case 'Run it 2x?':
      return '2';
    case 'Run it Thrice?':
    case 'Run it 3x?':
      return '3';
    case 'No':
      return '-2';
    default:
      return '0';
  }
};

/**
 * The player-facing label for a run count — AC-5.2's "RUN IT 1X / 2X / 3X",
 * never "twice" or "thrice".
 *
 * It lives beside the wire parser rather than in either renderer because BOTH
 * renderers need it: the vote buttons (VoteRunAction) and the seat speech
 * bubbles (SeatAction). AC-5.2 is a same-label-everywhere rule, so one copy of
 * the format is the only way it cannot quietly stop being true.
 */
export const runLabel = (runs: number): string => `RUN IT ${runs}X`;

/** Run count per wire vote value. 1X and "decline" are the same choice (AC-5.2). */
const RUN_FOR_VOTE: Record<string, number> = { '-2': 1, '2': 2, '3': 3 };

/**
 * The run count a seat's `p{seat}action` token names, or 0 when it names none.
 *
 * Built on `voteFromSeatAction` so the bubble reads the wire exactly as the
 * button-arming logic does — including its one-way tolerance for the briefly
 * shipped 'Run it 2x?' spelling. A second switch here would be a second chance
 * to disagree about whether a seat has voted.
 */
export const runFromSeatAction = (action?: string): number =>
  RUN_FOR_VOTE[voteFromSeatAction(action)] ?? 0;
